import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {IReactionDisposer, makeAutoObservable, observable, reaction} from "mobx";
import {Bindings} from "data/constants/bindings";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import {LeaderboardType, ModalType} from "data/enums";
import {AxiosError} from "axios";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IRanking} from "data/types/rankings";
import type {IContestsStore, IContest} from "data/stores/contests/contestsStore";
import type {IApiResponse} from "data/services/http";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IUserRosterStore} from "data/stores/user_roster/user_roster.store";

export interface ILeaderboardController extends ViewController {
	get i18n(): ILocalizationStore;

	get hasNext(): boolean;

	get contestId(): number;

	get isLoading(): boolean;

	get isUserInViewPort(): boolean;

	get rankings(): IRanking[];

	get userRanking(): IRanking | null;

	onLoadMore: () => void;
}

@injectable()
export class LeaderboardController implements ILeaderboardController {
	@observable private _subscriptions$: IReactionDisposer[] = [];
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RankingsStore) private readonly _rankingsStore: IRankingsStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.RoundsStore) private readonly _roundsStore: IRoundsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserRosterStore) private _userRosterStore: IUserRosterStore
	) {
		makeAutoObservable(this);
	}

	get hasNext(): boolean {
		return this._rankingsStore.nextPage;
	}

	get rankings(): IRanking[] {
		return this._rankingsStore.rankings;
	}

	get userRanking(): IRanking | null {
		return this._rankingsStore.userRankings;
	}

	get isLoading(): boolean {
		return this._rankingsStore.isLoading;
	}

	get isUserInViewPort(): boolean {
		return !!this.rankings.find((it) => it.userId === this.userRanking?.userId);
	}

	private get contest(): IContest | undefined {
		return this._contestsStore.currentContest;
	}

	get contestId(): number {
		return this.contest?.id ?? 0;
	}

	private get roundId(): number | undefined {
		return this.contest?.roundId || this._roundsStore.activeRound?.id;
	}

	private get isDailyLeaderboard(): boolean {
		return this._rankingsStore.activeLeaderboardType === LeaderboardType.Daily;
	}

	dispose(): void {
		this._subscriptions$.forEach((dispose) => dispose());
		this._contestsStore.clearContest();
		this._rankingsStore.clearRankings();
	}

	init(): void {
		const contestSubscription = reaction(
			() => [this.contestId, this._rankingsStore.activeLeaderboardType],
			() => {
				this.clearRankings();
				this._fetchLeaderboard();
				this.clearUserRosters();
			},
			{fireImmediately: true}
		);

		this._subscriptions$.push(contestSubscription);
	}

	onLoadMore = () => {
		if (this.isDailyLeaderboard) {
			const contestId = this.contestId;
			if (!contestId) {
				return;
			}
			this._rankingsStore.fetchMoreContestRankings(contestId).catch(this.onError);
		} else {
			const roundId = this.roundId;
			if (!roundId) {
				return;
			}
			this._rankingsStore.fetchMoreRoundRankings(roundId).catch(this.onError);
		}
	};

	private _fetchLeaderboard() {
		if (this.isDailyLeaderboard) {
			const contestId = this.contestId;
			if (!contestId) {
				return;
			}
			this._rankingsStore.fetchContestRankings(contestId).catch(this.onError);
		} else {
			const roundId = this.roundId;
			if (!roundId) {
				return;
			}
			this._rankingsStore.fetchRoundRankings(roundId).catch(this.onError);
		}
	}

	private onError = (error: AxiosError<IApiResponse>) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
		trackSentryErrors(error, {}, "leaderboards");
	};

	private clearRankings() {
		this._rankingsStore.clearRankings();
	}

	private clearUserRosters() {
		this._userRosterStore.clear();
	}
}
